/**
 * rewardTiers — pure tier-unlock logic for the 100% endings reward track (MY-1991).
 *
 * No Phaser import — fully testable in Node.
 *
 * Three tiers based on unique endings seen across all playthroughs:
 *   5/14  → bonus costume/skin set
 *   10/14 → Boss Rush unlock + secret weapon for NG+
 *   14/14 → playable Praetor + MANOR Mode + True End cutscene + badge/palette/dev-room
 *
 * OP is intentional — these are victory-lap rewards granted after mastery.
 * MANOR Mode and Praetor runs MUST be excluded from Score-Attack leaderboards.
 */

/** Stable id for THE PRAETOR hero, mirrors heroes.ts. */
export const PRAETOR_HERO_ID = 'praetor';

/** Stable id for THE RT HON. HALLOWAY (joke-PM playable), mirrors heroes.ts. */
export const HALLOWAY_HERO_ID = 'halloway';

/** Id of the secret weapon unlocked at 10/14 (usable in NG+). */
export const SECRET_WEAPON_ID = 'commons-mace';

export interface RewardTierDef {
  /** Number of unique endings required. */
  threshold: number;
  /** Short display label. */
  label: string;
  /** Stable reward ids granted at this tier. */
  rewards: readonly string[];
}

export const REWARD_TIERS: readonly RewardTierDef[] = [
  {
    threshold: 5,
    label: 'Sharp Dresser',
    rewards: ['costume_set'],
  },
  {
    threshold: 10,
    label: 'Completionist',
    rewards: ['boss_rush', SECRET_WEAPON_ID],
  },
  {
    threshold: 14,
    label: 'Manor 100%',
    rewards: [
      'praetor_playable',
      'halloway_playable',
      'manor_mode',
      'true_end_cutscene',
      'badge_100pct',
    ],
  },
];

/** Total number of endings in the game. */
export const TOTAL_ENDINGS = 14;

// ── Tier queries ──────────────────────────────────────────────────────────────

/** Returns thresholds for all tiers the player has earned. */
export function computeGrantedTiers(endingsSeenCount: number): number[] {
  return REWARD_TIERS.filter((t) => endingsSeenCount >= t.threshold).map((t) => t.threshold);
}

/** Returns thresholds that are newly earned vs what was previously granted. */
export function computeNewTiers(
  endingsSeenCount: number,
  alreadyGranted: readonly number[],
): number[] {
  return computeGrantedTiers(endingsSeenCount).filter((t) => !alreadyGranted.includes(t));
}

// ── Individual reward checks ──────────────────────────────────────────────────

/** 5/14: Bonus costume/skin set available. */
export function isCostumeSetUnlocked(endingsSeenCount: number): boolean {
  return endingsSeenCount >= 5;
}

/** 10/14: Boss Rush mode unlocked. */
export function isBossRushUnlocked(endingsSeenCount: number): boolean {
  return endingsSeenCount >= 10;
}

/** 10/14: Secret weapon usable in NG+. */
export function isSecretWeaponUnlocked(endingsSeenCount: number): boolean {
  return endingsSeenCount >= 10;
}

/** 14/14: THE PRAETOR selectable as a playable character. */
export function isPraetorUnlocked(endingsSeenCount: number): boolean {
  return endingsSeenCount >= TOTAL_ENDINGS;
}

/** 14/14: THE RT HON. HALLOWAY selectable as a playable character (joke handicap). */
export function isHallowayUnlocked(endingsSeenCount: number): boolean {
  return endingsSeenCount >= TOTAL_ENDINGS;
}

/** 14/14: MANOR Mode toggle available. */
export function isManorModeAvailable(endingsSeenCount: number): boolean {
  return endingsSeenCount >= TOTAL_ENDINGS;
}

/**
 * 14/14: True End cutscene should play.
 * Returns true only when the tier is earned AND the cutscene has not been seen yet.
 */
export function isTrueEndAvailable(endingsSeenCount: number, trueEndSeen: boolean): boolean {
  return endingsSeenCount >= TOTAL_ENDINGS && !trueEndSeen;
}

// ── Leaderboard flag ──────────────────────────────────────────────────────────

/**
 * Returns true when the run should be flagged/excluded from Score-Attack
 * leaderboard submissions.
 *
 * MANOR Mode (maxed stats + infinite special), Praetor (boss-tier kit), and
 * the Commons Mace (trivialises standard enemies) are excluded by design.
 * Halloway is intentionally NOT excluded — he is a handicap, not an advantage.
 */
export function isLeaderboardFlagged(
  manorModeEnabled: boolean,
  heroId: string,
  secretWeaponActive = false,
): boolean {
  return manorModeEnabled || heroId === PRAETOR_HERO_ID || secretWeaponActive;
}
